/**
 * Engine-neutral phone voice profile (Prompt 2.4, design §6.4). Stored in the
 * agent's voice config `settings.profile`; saving never touches the Vapi
 * web-voice assistant. Options come from the ACTIVE native implementation so
 * the dialog only offers what will actually be applied.
 */
import { get, post, put } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';
import { ApiError, isApiError } from '@core/api/types';

export type AgentVoiceEngine = 'orlenn' | 'vapi';
export type TtsProvider = 'elevenlabs' | 'google' | 'amazon';
export type SttProvider = 'deepgram' | 'google';
export type Sensitivity = 'low' | 'medium' | 'high';
export type NativeImpl = 'conversation_relay' | 'livekit';

export interface VoiceProfile {
  version: 1;
  engine: AgentVoiceEngine;
  language: string;
  tts: { provider: TtsProvider; voiceId: string | null; model: string | null };
  stt: { provider: SttProvider; model: string | null; hints: string[] };
  turnTaking: {
    sensitivity: Sensitivity;
    advanced: {
      relay: {
        speechTimeoutMs?: number;
        eotThreshold?: number;
        ignoreBackchannel?: boolean;
      };
      livekit: {
        minEndpointingDelayMs?: number;
        maxEndpointingDelayMs?: number;
        minInterruptionWords?: number;
      };
    };
  };
  noiseCancellation: boolean;
  firstMessage: string | null;
  /** Reworded AI disclosure spoken before the greeting; null → the default. */
  aiDisclosure: string | null;
  fillers: { enabled: boolean; phrases: string[] };
  nativeImplOverride: NativeImpl | null;
}

export interface AgentNumber {
  id: string;
  e164: string;
  label: string | null;
  status: string;
}

export interface AgentVoiceProfileView {
  agentId: string;
  profile: VoiceProfile;
  /** false: derived from the older voice settings — nothing saved yet. */
  stored: boolean;
  nativeImpl: NativeImpl;
  nativeImplLabel: string;
  overridesEnabled: boolean;
  vapi: {
    available: boolean;
    note: string;
    /** 3.4: Vapi is effective on phone calls (deployment flag + tenant SIP number). */
    phone?: { engineEnabled: boolean; sipReady: boolean };
  };
  numbers: AgentNumber[];
}

export interface CatalogVoice {
  voiceId: string;
  name: string;
  category: string | null;
  labels: Record<string, string>;
  description: string | null;
  previewUrl: string | null;
}

export interface AdvancedField {
  key: string;
  label: string;
  kind: 'ms' | 'ratio' | 'boolean' | 'count';
  min?: number;
  max?: number;
  help: string;
}

export interface VoiceOptions {
  nativeImpl: NativeImpl;
  nativeImplLabel: string;
  languages: Array<{ code: string; label: string }>;
  tts: {
    providers: TtsProvider[];
    models: string[];
    voices: CatalogVoice[];
    voicesAvailable: boolean;
    voicesError: string | null;
    previewAvailable: boolean;
  };
  stt: {
    providers: SttProvider[];
    models: Partial<Record<SttProvider, string[]>>;
  };
  turnTaking: { sensitivities: Sensitivity[]; advanced: AdvancedField[] };
  noiseCancellation: boolean;
  fillers: boolean;
  engines: {
    orlenn: { available: true };
    vapi: { available: boolean; note: string };
  };
}

export const agentVoiceProfileService = {
  get(agentId: string): Promise<AgentVoiceProfileView> {
    return get<AgentVoiceProfileView>(endpoints.agentVoiceProfile.profile(agentId));
  },
  save(
    agentId: string,
    profile: VoiceProfile,
  ): Promise<AgentVoiceProfileView & { recompiled: number }> {
    return put(endpoints.agentVoiceProfile.profile(agentId), profile);
  },
  options(agentId: string): Promise<VoiceOptions> {
    return get<VoiceOptions>(endpoints.agentVoiceProfile.options(agentId));
  },
  /** A short MP3 sample of the voice (platform ElevenLabs key, rate-limited). */
  preview(
    agentId: string,
    body: { voiceId: string; text?: string; model?: string; language?: string },
  ): Promise<Blob> {
    return post<Blob>(endpoints.agentVoiceProfile.preview(agentId), body, {
      responseType: 'blob',
    }).catch(async (err: unknown) => {
      throw await readBlobError(err);
    });
  },
} as const;

/**
 * With `responseType: 'blob'` an error body arrives as a Blob, so the client
 * cannot read its message — decode the JSON here (429 / 503 copy matters).
 */
async function readBlobError(err: unknown): Promise<unknown> {
  if (!isApiError(err) || !(err.details instanceof Blob)) return err;
  try {
    const data = JSON.parse(await err.details.text()) as {
      message?: unknown;
      code?: unknown;
    };
    const message = typeof data.message === 'string' ? data.message : err.message;
    const code = typeof data.code === 'string' ? data.code : 'UNKNOWN_ERROR';
    return new ApiError(err.status, code, message, data);
  } catch {
    return err;
  }
}
